import { Icon } from 'leaflet';
import { ParksAndTreesAssetType } from '@parks/hooks/useParksStore';

export type MapProps = [longitude: number, latitude: number] | null;

/**
 * Refer to here the list of AssetType enum: https://wcc-dev-apim-external.developer.azure-api.net/api-details#api=wcc-gis-service-api&operation=findnearestasset
 */
export type AssetType =
  | 'PublicSpaces'
  | 'ExternalPublicSpaces'
  | 'Trees'
  | 'Cemeteries'
  | 'Parks'
  | 'Playgrounds'
  | 'TfLRedRoutesTLRNs'
  | 'Westminster1kmBoundaryBuffer'
  | 'CycleHangarsEstates'
  | 'CycleHangars'
  | 'TfLHighwayAuthorityBoundary'
  | 'WestminsterBoundary'
  | 'SmokeAndOdours'
  | 'Noise'
  | 'HighwaysCarriageways'
  | 'HighwaysFootways'
  | 'HighwaysGullies'
  | 'PublicLightingStreetlights'
  | 'PropertyGazetteer'
  | 'PublicLightingStreetlightsEnquiries'
  | 'HighwaysGulliesEnquiries'
  | 'HighwaysCarriagewaysEnquiries'
  | 'HighwaysFootwaysEnquiries'
  | 'Mask'
  | 'StreetGazetteer'
  | 'HighwaysBollards'
  | 'HighwaysBollardsEnquiries'
  | 'HighwaysKerbs'
  | 'HighwaysKerbsEnquiries'
  | 'HighwaysNameplates'
  | 'HighwaysNameplatesEnquiries'
  | 'HighwaysSigns'
  | 'HighwaysSignsEnquiries'
  | 'PublicLightingBelishaBeacons'
  | 'PublicLightingBelishaBeaconsEnquiries'
  | 'PublicLightingBollards'
  | 'PublicLightingBollardsEnquiries'
  | 'PublicLightingFeederPillars'
  | 'PublicLightingFeederPillarsEnquiries'
  | 'PublicLightingIlluminatedSigns'
  | 'PublicLightingIlluminatedSignsEnquiries'
  | 'PublicLightingWallLights'
  | 'PublicLightingWallLightsEnquiries'
  | 'HighwaysWholeSiteFeatures'
  | 'DrainageWholeSiteFeatures'
  | 'PublicLightingWholeSitefeatures'
  | 'StreetEntertainmentPitches'
  | 'MarketPitches'
  | 'StreetEntertainmentRegulatedAreas'
  | 'PublicLightingElectricVehicleChargers'
  | 'PublicLightingElectricVehicleChargersEnquiries'
  | 'ListedBuildings'
  | 'PlaySpaces'
  | 'PublicParksAndGardens'
  | 'Wards';

/**
 * Refer to - https://www.rbkc.gov.uk/arcgis/rest/services/WCC/FixMyStreetOpen/MapServer
 */
export const assetTypeId: { [key in AssetType]: string } = {
  PublicSpaces: '553',
  ExternalPublicSpaces: '1',
  MarketPitches: '548',
  Trees: '552',
  Cemeteries: '560',
  Parks: '545',
  Playgrounds: '546',
  TfLRedRoutesTLRNs: '2',
  Westminster1kmBoundaryBuffer: '542',
  CycleHangarsEstates: '543',
  CycleHangars: '444',
  TfLHighwayAuthorityBoundary: '3',
  WestminsterBoundary: '4',
  SmokeAndOdours: '6',
  Noise: '7',
  HighwaysCarriageways: '11',
  HighwaysFootways: '14',
  HighwaysGullies: '16',
  PublicLightingStreetlights: '18',
  PropertyGazetteer: '25',
  PublicLightingStreetlightsEnquiries: '27',
  HighwaysGulliesEnquiries: '28',
  HighwaysCarriagewaysEnquiries: '34',
  HighwaysFootwaysEnquiries: '36',
  Mask: '38',
  StreetGazetteer: '40',
  HighwaysBollards: '42',
  HighwaysBollardsEnquiries: '43',
  HighwaysKerbs: '44',
  HighwaysKerbsEnquiries: '45',
  HighwaysNameplates: '46',
  HighwaysNameplatesEnquiries: '47',
  HighwaysSigns: '48',
  HighwaysSignsEnquiries: '49',
  PublicLightingBelishaBeacons: '50',
  PublicLightingBelishaBeaconsEnquiries: '51',
  PublicLightingBollards: '52',
  PublicLightingBollardsEnquiries: '53',
  PublicLightingFeederPillars: '56',
  PublicLightingFeederPillarsEnquiries: '57',
  PublicLightingIlluminatedSigns: '58',
  PublicLightingIlluminatedSignsEnquiries: '59',
  PublicLightingWallLights: '60',
  PublicLightingWallLightsEnquiries: '61',
  HighwaysWholeSiteFeatures: '62',
  DrainageWholeSiteFeatures: '63',
  PublicLightingWholeSitefeatures: '64',
  StreetEntertainmentPitches: '66',
  StreetEntertainmentRegulatedAreas: '67',
  PublicLightingElectricVehicleChargers: '69',
  PublicLightingElectricVehicleChargersEnquiries: '70',
  ListedBuildings: '559',
  PlaySpaces: '546',
  PublicParksAndGardens: '545',
  Wards: '551',
};

export interface AssetConfig {
  asset: AssetType;
  icon?:
    | {
        shape?: 'circle' | 'square' | 'triangle';
        size?: number;
      }
    | Icon;
  color?: string;
  opacity?: number;
  strokeColor?: string;
  fillColor?: string;
  fillOpacity?: number;
  weight?: number;
  containsAssetsOutsideOfBoundary?: boolean;
  onAdd?: (geojson: any, marker: L.Marker<any>) => void;
}

export type AssetTypeConfig = AssetType | AssetConfig;

export interface NearestApiResponse {
  within_boundary: boolean;
  within_tfl_boundary: boolean;
  features: Feature[];
}

interface Feature {
  attributes:
    | HighwaysCarriagewaysFeatureAttributes
    | CycleHangarsFeatureAttributes
    | CycleHangarsEstatesFeatureAttributes
    | ParksAndTreesAssetType;
  geometry: FeatureGeometry;
}

interface FeatureGeometry {
  paths: Coordinate[][];
}

type Coordinate = [number, number];

export interface HighwaysCarriagewaysFeatureAttributes {
  site_class_code: string;
  site_class_name: string;
  central_asset_id: string;
  contract_area_code: string;
  feature_type_code: string;
  layer: AssetType;
  site_name?: string;
}

export interface CycleHangarsFeatureAttributes {
  Application_URL: string;
  Available_Spaces: number;
  CH_Ref: string;
  Data_Custodian: string;
  Eastings: number;
  Footway_Carriageway: string;
  GIS_Load_Date: number;
  GIS_Ref: number;
  Grid_Reference: string;
  Hangar_Capacity: number;
  Installation_Date: number;
  Latitude: number;
  Location: string;
  Longitude: number;
  Northings: number;
  OBJECTID: number;
  Postcode: string;
  Street_Name: string;
}

export interface CycleHangarsEstatesFeatureAttributes {
  Application_URL: string;
  Available_Spaces: number;
  Comments: string;
  Eastings: number;
  Estate_Name: string;
  Footway_Carriageway: string;
  GISLoadDate: number;
  GIS_Ref: number;
  Installation_Date: number;
  Latitude: number;
  Location: string;
  Longitude: number;
  Northings: number;
  OBJECTID: number;
  Postcode: string;
  Street_Name: string;
  Unit_ID: string;
}

export interface CemeteryAssetFeatureAttributes {
  Centre_Latitude: number;
  Centre_Longitude: number;
  MapRef: number;
  Name: string;
  OBJECTID: number;
  OBJECTID_1: number;
  Owner: string;
}

export interface TreeAssetFeatureAttributes {
  Age: string; // e.g., "Young Tree"
  CommonName: string; // e.g., "Snowy Mespil"
  GISLoadDate: number; // Unix timestamp in milliseconds
  Location: string; // e.g., "9"
  OBJECTID: number; // e.g., 16013
  Site: string; // e.g., "New Burlington Place"
  Species: string; // e.g., "Amelanchier"
  TreeCode: string; // e.g., "123016"
  X: number; // e.g., 529078.81
  Y: number; // e.g., 180886.88
}

export interface ParkAndPlayspaceAssetFeatureAttributes {
  OBJECTID: number; // e.g., 202
  AssetId: number; // e.g., 202
  Owner: number; // e.g., 1
  Region: string; // e.g., "Central"
  Type: string; // e.g., "Park"
  Name: string; // e.g., "Bayswater Road"
}

export interface PublicLightingElectricVehicleChargers {
  OBJECTID: number;
  site_code: string;
  site_name: string;
  site_uprn: string;
  site_class_code: string;
  site_class_name: string;
  central_asset_id: string;
  asset_number: string;
  contract_area_code: string;
  feature_deadflag: string;
  feature_type_code: string;
  feat_ext_1_east: number;
  feat_ext_1_north: number;
  feat_ext_2_east: number;
  feat_ext_2_north: number;
  feat_cent_east: number;
  feat_cent_north: number;
  wsf: string;
  wsl: string;
  wsg: string;
  SHAPE: string;
}

export type GulliesAsset = {
  OBJECTID: number;
  asset_number: string;
  central_asset_id: string;
  contract_area_code: string;
  feat_cent_east: number;
  feat_cent_north: number;
  feat_ext_1_east: number;
  feat_ext_1_north: number;
  feat_ext_2_east: number;
  feat_ext_2_north: number;
  feature_deadflag: string;
  feature_type_code: string;
  site_class_code: string;
  site_class_name: string;
  site_code: string;
  site_name: string;
  site_uprn: string;
  wsf: string;
  wsg: string;
  wsl: string;
};

export type BasicAssetType = {
  central_asset_id: string;
  site_name: string;
  site_class_name: string;
  feature_type_code: string;
};
